import React, { useEffect, useState } from "react";
import MultipleImage from "../component/MultipleImage";
import { useDispatch, useSelector } from "react-redux";
import Select from "../component/SelectSearch";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchProductById,
  updateProduct,
} from "../../../slice/product/productSlice";
import { fetchCategoryDropdown } from "../../../slice/category/categorySlice";
import {
  fetchSubCategoriesByCategory,
  clearSubCategories,
} from "../../../slice/subCategory/subCategorySlice";
import { fetchSizes }  from "../../../slice/size/sizeSlice";
import { fetchColors } from "../../../slice/color/colorSlice";
import { fetchLabels } from "../../../slice/label/labelSlice";
import AdminZoneDrawer from "./Adminzonedrawer";

/* Portrait comes first — same as AddProduct */
const ORIENTATIONS = ["portrait", "landscape"];

const emptyOrientation = () => ({
  productImages:   [],
  existingImages:  [],
  previewFile:     null,
  previewPreview:  null,
  existingPreview: null,
  photoZones:      [],
  textZones:       [],
});

const emptyColorEntry = (colorId) => ({
  colorId,
  landscape: emptyOrientation(),
  portrait:  emptyOrientation(),
});

export default function EditProduct() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();

  const { singleProduct }                               = useSelector((s) => s.product);
  const { dropdownCategories, loading }                 = useSelector((s) => s.category);
  const { subCategories = [], loading: subLoading }     = useSelector((s) => s.subCategory || {});
  const { sizes = [],  loading: sizeLoading }           = useSelector((s) => s.size);
  const { colors = [], loading: colorLoading }          = useSelector((s) => s.color);
  const { labels = [], loading: labelLoading }          = useSelector((s) => s.label || {});

  const [form, setForm] = useState({
    categoryId: "", subCategoryId: "", name: "", description: "",
    sizes: [], colors: [], textCount: "", imageCount: "", label: "", status: true,
  });
  const [selectedSizeIds,   setSelectedSizeIds]   = useState([]);
  const [errors,            setErrors]            = useState({});
  const [colorData,         setColorData]         = useState({});
  const [activeColorId,     setActiveColorId]     = useState(null);
  const [activeOrientation, setActiveOrientation] = useState("portrait"); // ← portrait first
  const [dataLoaded,        setDataLoaded]        = useState(false);

  const categoryOptions    = dropdownCategories?.map((c) => ({ value: c._id, label: c.name })) || [];
  const subCategoryOptions = form.categoryId
    ? subCategories?.map((s) => ({ value: s._id, label: s.name })) || []
    : [];

  useEffect(() => {
    dispatch(fetchCategoryDropdown());
    dispatch(fetchSizes({ page: 1, limit: 100 }));
    dispatch(fetchColors({ page: 1, limit: 100 }));
    dispatch(fetchLabels({ page: 1, limit: 100 }));
    dispatch(fetchProductById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (form.categoryId) dispatch(fetchSubCategoriesByCategory(form.categoryId));
  }, [form.categoryId, dispatch]);

  useEffect(() => {
    if (!singleProduct || sizes.length === 0 || dataLoaded) return;

    const savedSizeMap = {};
    singleProduct.sizes?.forEach((s) => {
      const sid = s.size?._id || s.size || s._id;
      savedSizeMap[sid] = { mrp: s.mrp ?? "", sellingPrice: s.sellingPrice ?? "" };
    });
    const mappedSizes   = sizes.filter((sz) => savedSizeMap[sz._id]).map((sz) => ({
      sizeId: sz._id, mrp: savedSizeMap[sz._id].mrp, sellingPrice: savedSizeMap[sz._id].sellingPrice,
    }));
    const activeSizeIds = sizes.filter((sz) => savedSizeMap[sz._id]).map((sz) => sz._id);
    setSelectedSizeIds(activeSizeIds);

    const colorIds = singleProduct.colors?.map((c) => c._id || c) || [];

    const newColorData = {};
    colorIds.forEach((cid) => {
      const saved = singleProduct.colorVariants?.find(
        (v) => String(v.colorId?._id || v.colorId) === String(cid)
      );
      const buildOri = (ori) => ({
        productImages:   [],
        existingImages:  ori?.images       || [],
        previewFile:     null,
        previewPreview:  null,
        existingPreview: ori?.previewImage || null,
        photoZones:      ori?.photoZones   || [],
        textZones:       ori?.textZones    || [],
      });
      newColorData[cid] = {
        colorId:   cid,
        landscape: buildOri(saved?.landscape),
        portrait:  buildOri(saved?.portrait),
      };
    });

    setColorData(newColorData);
    if (colorIds.length) {
      setActiveColorId(colorIds[0]);
      setActiveOrientation("portrait"); // ← reset to portrait on data load
    }

    setForm({
      categoryId:    singleProduct.category?._id    || "",
      subCategoryId: singleProduct.subCategory?._id || "",
      name:          singleProduct.name        || "",
      description:   singleProduct.description || "",
      sizes:         mappedSizes,
      colors:        colorIds,
      textCount:     singleProduct.textCount  ?? "",
      imageCount:    singleProduct.imageCount ?? "",
      label:         singleProduct.label?._id || "",
      status:        singleProduct.status ?? true,
    });

    if (singleProduct.category?._id)
      dispatch(fetchSubCategoriesByCategory(singleProduct.category._id));

    setDataLoaded(true);
  }, [singleProduct, sizes, dataLoaded, dispatch]);

  const updateOri = (colorId, orientation, patch) => {
    setColorData((prev) => ({
      ...prev,
      [colorId]: {
        ...prev[colorId],
        [orientation]: { ...prev[colorId]?.[orientation], ...patch },
      },
    }));
  };

  const handleColorChange = (colorId) => {
    setForm((prev) => {
      const isSelected = prev.colors.includes(colorId);
      const newColors  = isSelected
        ? prev.colors.filter((c) => c !== colorId)
        : [...prev.colors, colorId];

      setColorData((cd) => {
        const next = { ...cd };
        if (isSelected) { delete next[colorId]; }
        else if (!next[colorId]) { next[colorId] = emptyColorEntry(colorId); }
        return next;
      });

      if (!isSelected && !activeColorId) {
        setActiveColorId(colorId);
        setActiveOrientation("portrait"); // ← reset to portrait when new color added
      }
      if (isSelected && activeColorId === colorId) {
        const remaining = newColors.filter((c) => c !== colorId);
        setActiveColorId(remaining[0] || null);
        setActiveOrientation("portrait"); // ← reset to portrait when switching
      }

      return { ...prev, colors: newColors };
    });
    if (errors.colors) setErrors((prev) => ({ ...prev, colors: undefined }));
  };

  // ← Reset orientation to portrait whenever active color tab changes
  const handleColorTabClick = (cid) => {
    setActiveColorId(cid);
    setActiveOrientation("portrait");
  };

  const handleSizeToggle = (sizeId) => {
    const isSelected = selectedSizeIds.includes(sizeId);
    if (isSelected) {
      setSelectedSizeIds((prev) => prev.filter((s) => s !== sizeId));
      setForm((prev) => ({ ...prev, sizes: prev.sizes.filter((s) => s.sizeId !== sizeId) }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`size_mrp_${sizeId}`];
        delete next[`size_sp_${sizeId}`];
        return next;
      });
    } else {
      setSelectedSizeIds((prev) => [...prev, sizeId]);
      setForm((prev) => ({ ...prev, sizes: [...prev.sizes, { sizeId, mrp: "", sellingPrice: "" }] }));
    }
  };

  const handleSizePriceChange = (sizeId, field, value) => {
    setForm((prev) => ({
      ...prev,
      sizes: prev.sizes.map((s) => s.sizeId === sizeId ? { ...s, [field]: value } : s),
    }));
    const key = field === "mrp" ? `size_mrp_${sizeId}` : `size_sp_${sizeId}`;
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.categoryId)    newErrors.categoryId    = "Category is required";
    if (!form.subCategoryId) newErrors.subCategoryId = "Sub-category is required";
    if (!form.name.trim())   newErrors.name          = "Product name is required";
    if (!form.colors.length) newErrors.colors        = "Please select at least one color";
    if (!form.textCount)     newErrors.textCount     = "Text count is required";
    if (!form.imageCount || Number(form.imageCount) <= 0)
      newErrors.imageCount = "Image upload count is required";
    if (selectedSizeIds.length === 0)
      newErrors.sizes = "Please select at least one size";

    form.sizes.forEach((s) => {
      if (!s.mrp          || Number(s.mrp)          <= 0) newErrors[`size_mrp_${s.sizeId}`] = "MRP is required";
      if (!s.sellingPrice || Number(s.sellingPrice) <= 0) newErrors[`size_sp_${s.sizeId}`]  = "Selling price is required";
      if (s.mrp && s.sellingPrice && Number(s.sellingPrice) > Number(s.mrp))
        newErrors[`size_sp_${s.sizeId}`] = "Selling price cannot exceed MRP";
    });

    const imgCount = Number(form.imageCount) || 0;
    const txtCount = Number(form.textCount)  || 0;

    form.colors.forEach((cid) => {
      const entry    = colorData[cid];
      const colorObj = colors.find((c) => c._id === cid);
      const cName    = colorObj?.name || cid;

      ORIENTATIONS.forEach((ori) => {
        const o      = entry?.[ori];
        const prefix = `color_${cid}_${ori}`;

        const hasPreview = o?.previewPreview || o?.existingPreview;
        if (hasPreview) {
          if (imgCount > 0 && o?.photoZones?.length !== imgCount)
            newErrors[`${prefix}_photoZones`] = `${cName} (${ori}): draw exactly ${imgCount} photo zone(s) (${o?.photoZones?.length || 0} drawn)`;
          if (txtCount > 0 && o?.textZones?.length !== txtCount)
            newErrors[`${prefix}_textZones`]  = `${cName} (${ori}): draw exactly ${txtCount} text zone(s) (${o?.textZones?.length || 0} drawn)`;
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) { toast.error("Please fix all validation errors"); return; }

    try {
      const formData = new FormData();
      formData.append("name",        form.name);
      formData.append("category",    form.categoryId);
      if (form.subCategoryId) formData.append("subCategory", form.subCategoryId);
      formData.append("description", form.description || "");
      formData.append("textCount",   Number(form.textCount));
      formData.append("imageCount",  Number(form.imageCount));

      form.sizes.forEach((s) => {
        formData.append("sizeIds",           s.sizeId);
        formData.append("sizeMrps",          Number(s.mrp));
        formData.append("sizeSellingPrices", Number(s.sellingPrice));
      });

      form.colors.forEach((cid) => formData.append("colors", cid));
      if (form.label) formData.append("label", form.label);

      const colorVariantsPayload = form.colors.map((cid) => {
        const colorObj = colors.find((c) => c._id === cid);
        const entry = colorData[cid] || emptyColorEntry(cid);

        const result = {
          colorId:   cid,
          colorName: colorObj?.name || "",
          colorCode: colorObj?.code || "",
        };

        ["portrait", "landscape"].forEach((ori) => {
          const o = entry[ori] || emptyOrientation();
          result[ori] = {
            existingImages:       o.existingImages  || [],
            existingPreviewImage: o.existingPreview || null,
            photoZones:           o.photoZones      || [],
            textZones:            o.textZones       || [],
          };
        });

        return result;
      });

      formData.append("colorVariants", JSON.stringify(colorVariantsPayload));

      form.colors.forEach((cid, ci) => {
        ["portrait", "landscape"].forEach((ori) => {
          const o = colorData[cid]?.[ori];
          o?.productImages?.forEach((file) =>
            formData.append(`colorVariants[${ci}][${ori}][images]`, file)
          );
          if (o?.previewFile) {
            formData.append(`colorVariants[${ci}][${ori}][previewImage]`, o.previewFile);
          }
        });
      });

      await dispatch(updateProduct({ id, formData })).unwrap();
      toast.success("Product updated successfully!");
      navigate("/admin/product/list");
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err?.message || "Failed to update product");
    }
  };

  const imgCount = Number(form.imageCount) || 0;
  const txtCount = Number(form.textCount)  || 0;

  return (
    <div className="row mt-3">
      <div className="col-lg-12">

        <div className="backbtn">
          <button className="btn btn-info" onClick={() => navigate("/admin/product/list")}>
            <i className="bx bx-left-arrow-alt me-1"></i>back
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Edit Product</h4>
          </div>

          <div className="card-body">

            {!dataLoaded && (
              <div className="text-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status" />
                Loading product data…
              </div>
            )}

            {dataLoaded && (
              <>
                {/* ── CATEGORY / SUBCATEGORY / NAME ── */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Category</label>
                    <Select
                      options={categoryOptions}
                      placeholder={loading ? "Loading categories..." : "Select parent category"}
                      value={categoryOptions.find((o) => o.value === form.categoryId)}
                      onChange={(option) => {
                        const categoryId = option?.value || "";
                        setForm((prev) => ({ ...prev, categoryId, subCategoryId: "" }));
                        dispatch(clearSubCategories());
                        if (categoryId) dispatch(fetchSubCategoriesByCategory(categoryId));
                        if (errors.categoryId) setErrors((prev) => ({ ...prev, categoryId: undefined }));
                      }}
                    />
                    {errors.categoryId && <small className="text-danger">{errors.categoryId}</small>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Sub-Category</label>
                    <Select
                      options={subCategoryOptions}
                      placeholder={!form.categoryId ? "Select category first" : subLoading ? "Loading..." : "Select sub-category"}
                      isDisabled={!form.categoryId}
                      value={subCategoryOptions.find((o) => o.value === form.subCategoryId) || null}
                      onChange={(option) => {
                        setForm((prev) => ({ ...prev, subCategoryId: option?.value || "" }));
                        if (errors.subCategoryId) setErrors((prev) => ({ ...prev, subCategoryId: undefined }));
                      }}
                      isClearable
                    />
                    {errors.subCategoryId && <small className="text-danger">{errors.subCategoryId}</small>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Product Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? "is-invalid" : ""}`}
                      placeholder="Enter product name"
                      value={form.name}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, name: e.target.value }));
                        if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                </div>

                {/* ── DESCRIPTION ── */}
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Product description"
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                {/* ── SIZE OPTIONS ── */}
                <div className="mb-4">
                  <label className="form-label fw-semibold d-block">
                    Size Options &amp; Pricing <span className="text-danger">*</span>
                  </label>
                  <small className="text-muted d-block mb-3">
                    Select the sizes available for this product, then fill in MRP and Selling Price for each.
                  </small>

                  {sizeLoading ? <span>Loading sizes...</span> : (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {sizes.map((size) => {
                        const isSelected = selectedSizeIds.includes(size._id);
                        return (
                          <div key={size._id}>
                            <input type="checkbox" className="btn-check"
                              id={`size-toggle-${size._id}`} checked={isSelected}
                              onChange={() => handleSizeToggle(size._id)} />
                            <label className={`btn ${isSelected ? "btnSelect" : "btn-outline-primary"}`}
                              htmlFor={`size-toggle-${size._id}`}>
                              📐 {size.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {errors.sizes && (
                    <div className="text-danger mb-2" style={{ fontSize: "0.875rem" }}>{errors.sizes}</div>
                  )}

                  {selectedSizeIds.length > 0 && (
                    <div className="d-flex flex-wrap gap-3">
                      {form.sizes.map((sizeEntry) => {
                        const sizeInfo    = sizes.find((s) => s._id === sizeEntry.sizeId);
                        if (!sizeInfo) return null;
                        const hasMrpError = !!errors[`size_mrp_${sizeEntry.sizeId}`];
                        const hasSpError  = !!errors[`size_sp_${sizeEntry.sizeId}`];
                        const hasAnyError = hasMrpError || hasSpError;
                        return (
                          <div key={sizeEntry.sizeId} style={{
                            border: `2px solid ${hasAnyError ? "#dc3545" : "#0d6efd"}`,
                            borderRadius: "10px", padding: "14px 16px", minWidth: "210px",
                            background: hasAnyError ? "#fff5f5" : "#f0f5ff",
                          }}>
                            <div className="mb-3">
                              <span style={{ fontWeight: 700, fontSize: "14px", color: hasAnyError ? "#dc3545" : "#0d6efd" }}>
                                📐 {sizeInfo.name}
                              </span>
                            </div>
                            <div className="mb-2">
                              <label style={{ fontSize: "12px", color: "#6b7280", marginBottom: "2px", display: "block" }}>
                                MRP (₹) <span className="text-danger">*</span>
                              </label>
                              <input type="number" min="1"
                                className={`form-control form-control-sm ${hasMrpError ? "is-invalid" : ""}`}
                                placeholder="Enter MRP" value={sizeEntry.mrp}
                                onChange={(e) => handleSizePriceChange(sizeEntry.sizeId, "mrp", e.target.value)} />
                              {hasMrpError && (
                                <div className="invalid-feedback d-block" style={{ fontSize: "11px" }}>
                                  {errors[`size_mrp_${sizeEntry.sizeId}`]}
                                </div>
                              )}
                            </div>
                            <div>
                              <label style={{ fontSize: "12px", color: "#6b7280", marginBottom: "2px", display: "block" }}>
                                Selling Price (₹) <span className="text-danger">*</span>
                              </label>
                              <input type="number" min="1"
                                className={`form-control form-control-sm ${hasSpError ? "is-invalid" : ""}`}
                                placeholder="Enter Selling Price" value={sizeEntry.sellingPrice}
                                onChange={(e) => handleSizePriceChange(sizeEntry.sizeId, "sellingPrice", e.target.value)} />
                              {hasSpError && (
                                <div className="invalid-feedback d-block" style={{ fontSize: "11px" }}>
                                  {errors[`size_sp_${sizeEntry.sizeId}`]}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── TEXT COUNT & IMAGE COUNT ── */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Text Count <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${errors.textCount ? "is-invalid" : ""}`}
                      placeholder="Number of text zones"
                      value={form.textCount}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, textCount: e.target.value }));
                        if (errors.textCount) setErrors((prev) => ({ ...prev, textCount: undefined }));
                      }}
                    />
                    {errors.textCount && <div className="invalid-feedback">{errors.textCount}</div>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Image Upload Count <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      className={`form-control ${errors.imageCount ? "is-invalid" : ""}`}
                      placeholder="Number of photo zones"
                      value={form.imageCount}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, imageCount: e.target.value }));
                        if (errors.imageCount) setErrors((prev) => ({ ...prev, imageCount: undefined }));
                      }}
                    />
                    {errors.imageCount && <div className="invalid-feedback">{errors.imageCount}</div>}
                  </div>
                </div>

                {/* ── COLOR OPTIONS ── */}
                <div className="mb-4">
                  <label className="form-label fw-semibold d-block">
                    Color Options <span className="text-danger">*</span>
                  </label>
                  <small className="text-muted d-block mb-2">
                    Select colors — each color can optionally have Portrait &amp; Landscape images, a preview image, and zone mapping.
                  </small>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {colorLoading ? <span>Loading colors...</span> : (
                      colors.map((color) => (
                        <div key={color._id}>
                          <input type="checkbox" className="btn-check" id={`color-${color._id}`}
                            checked={form.colors.includes(color._id)}
                            onChange={() => handleColorChange(color._id)} />
                          <label className="btn btn-outline-secondary d-flex align-items-center gap-2"
                            htmlFor={`color-${color._id}`}>
                            <span className="rounded-circle"
                              style={{ width: "20px", height: "20px", backgroundColor: color.code, border: "1px solid #ccc" }} />
                            {color.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {errors.colors && <div className="text-danger mt-2" style={{ fontSize: "0.875rem" }}>{errors.colors}</div>}
                </div>

                {/* ── PER-COLOR UPLOAD SECTION ── */}
                {form.colors.length > 0 && (
                  <div className="mb-4">
                    <div style={{ borderTop: "2px solid #e5e7eb", marginBottom: "20px" }} />

                    <label className="form-label fw-semibold d-block mb-3" style={{ fontSize: "15px" }}>
                      🎨 Per-Color Images &amp; Zone Setup
                    </label>

                    {/* Color tab bar */}
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
                      {form.colors.map((cid) => {
                        const colorObj  = colors.find((c) => c._id === cid);
                        const isActive  = activeColorId === cid;
                        const hasErrors = ORIENTATIONS.some((ori) =>
                          errors[`color_${cid}_${ori}_photoZones`] ||
                          errors[`color_${cid}_${ori}_textZones`]
                        );
                        return (
                          <button key={cid} type="button"
                            onClick={() => handleColorTabClick(cid)}
                            style={{
                              display: "flex", alignItems: "center", gap: "8px",
                              padding: "8px 18px", borderRadius: "24px", fontWeight: 600, fontSize: "13px",
                              border: `2px solid ${hasErrors ? "#dc3545" : isActive ? colorObj?.code || "#0d6efd" : "#d1d5db"}`,
                              background: isActive ? (colorObj?.code ? `${colorObj.code}22` : "#f0f5ff") : "#f9fafb",
                              color: isActive ? "#1e293b" : "#6b7280",
                              cursor: "pointer", transition: "all 0.15s",
                              boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.10)" : "none",
                            }}>
                            <span style={{
                              width: "14px", height: "14px", borderRadius: "50%",
                              backgroundColor: colorObj?.code, border: "1.5px solid #ccc", flexShrink: 0,
                            }} />
                            {colorObj?.name || cid}
                            {hasErrors && <span style={{ color: "#dc3545", fontWeight: 700 }}>!</span>}
                          </button>
                        );
                      })}
                    </div>

                    {form.colors.map((cid) => {
                      const entry = colorData[cid] || emptyColorEntry(cid);
                      return (
                        <div key={cid} style={{
                          display: activeColorId === cid ? "block" : "none",
                          border: "2px solid #e5e7eb", borderRadius: "14px",
                          padding: "20px", background: "#fafafa",
                        }}>
                          {/* Orientation tabs — Portrait first */}
                          <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
                            {ORIENTATIONS.map((ori) => {
                              const isActive = activeOrientation === ori;
                              const oriEntry = entry[ori];
                              const hasImgs  = oriEntry?.productImages?.length > 0 || oriEntry?.existingImages?.length > 0;
                              const hasPrev  = oriEntry?.previewPreview || oriEntry?.existingPreview;
                              const complete = hasImgs || hasPrev;
                              const icon     = ori === "landscape" ? "🖼️" : "📱";
                              return (
                                <button key={ori} type="button" onClick={() => setActiveOrientation(ori)}
                                  style={{
                                    padding: "8px 20px", borderRadius: "8px", fontWeight: 600, fontSize: "13px",
                                    border: `2px solid ${isActive ? "#0d6efd" : "#d1d5db"}`,
                                    background: isActive ? "#f0f5ff" : "#fff",
                                    color: isActive ? "#0d6efd" : "#6b7280",
                                    cursor: "pointer", transition: "all 0.15s",
                                    display: "flex", alignItems: "center", gap: "6px",
                                  }}>
                                  {icon} {ori.charAt(0).toUpperCase() + ori.slice(1)}
                                  {complete && (
                                    <span style={{ fontSize: "10px", background: "#10b981", color: "#fff", borderRadius: "10px", padding: "1px 6px" }}>✓</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>

                          {ORIENTATIONS.map((ori) => (
                            <div key={ori} style={{ display: ori === activeOrientation ? "block" : "none" }}>
                              <OrientationPanel
                                colorId={cid}
                                orientation={ori}
                                oriEntry={entry[ori] || emptyOrientation()}
                                imgCount={imgCount}
                                txtCount={txtCount}
                                errors={errors}
                                onProductImagesChange={(files) => {
                                  updateOri(cid, ori, { productImages: files });
                                }}
                                onRemoveExistingImage={(idx) => {
                                  updateOri(cid, ori, {
                                    existingImages: (colorData[cid]?.[ori]?.existingImages || []).filter((_, i) => i !== idx),
                                  });
                                }}
                                onPreviewChange={(file, preview) => {
                                  updateOri(cid, ori, {
                                    previewFile: file, previewPreview: preview,
                                    existingPreview: null, photoZones: [], textZones: [],
                                  });
                                }}
                                onRemovePreview={() => {
                                  updateOri(cid, ori, {
                                    previewFile: null, previewPreview: null,
                                    existingPreview: null, photoZones: [], textZones: [],
                                  });
                                }}
                                onZonesChange={({ photo, text }) => {
                                  updateOri(cid, ori, { photoZones: photo, textZones: text });
                                  setErrors((prev) => ({
                                    ...prev,
                                    [`color_${cid}_${ori}_photoZones`]: undefined,
                                    [`color_${cid}_${ori}_textZones`]:  undefined,
                                  }));
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── PRODUCT LABEL ── */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Product Label</label>
                  <div className="d-flex flex-wrap gap-3">
                    {labelLoading ? <span>Loading labels...</span> :
                     labels.length === 0 ? <small className="text-muted">No labels available</small> : (
                      labels.filter((l) => l.status).map((lbl) => (
                        <div key={lbl._id} className="form-check">
                          <input className="form-check-input" type="checkbox" id={`label-${lbl._id}`}
                            checked={form.label === lbl._id}
                            onChange={() => setForm((prev) => ({ ...prev, label: prev.label === lbl._id ? "" : lbl._id }))}
                          />
                          <label className="form-check-label" htmlFor={`label-${lbl._id}`}>{lbl.name}</label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* ── SAVE ── */}
                <div className="text-end">
                  <button className="btn cuscancel" onClick={() => navigate("/admin/product/list")}>Cancel</button>
                  &nbsp;
                  <button className="btn cusBTNPop" onClick={handleSubmit}>Update Product</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   OrientationPanel
──────────────────────────────────────────────────────────────────── */
function OrientationPanel({
  colorId, orientation, oriEntry,
  imgCount, txtCount, errors,
  onProductImagesChange,
  onRemoveExistingImage,
  onPreviewChange,
  onRemovePreview,
  onZonesChange,
}) {
  const errPrefix  = `color_${colorId}_${orientation}`;
  const previewSrc = oriEntry.previewPreview
    || (oriEntry.existingPreview
      ? `${process.env.REACT_APP_API_URL}/uploads/${oriEntry.existingPreview}`
      : null);

  const handlePreviewInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onPreviewChange(file, URL.createObjectURL(file));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

      {/* ── Existing product images with remove buttons ── */}
      {oriEntry.existingImages?.length > 0 && (
        <div>
          <label className="form-label fw-semibold">
            Current Images ({orientation})
          </label>
          <div className="d-flex flex-wrap gap-2 mt-1">
            {oriEntry.existingImages.map((img, idx) => (
              <div key={idx} style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={`${process.env.REACT_APP_API_URL}/uploads/${img}`}
                  alt={`existing-${idx}`}
                  style={{
                    width: "90px", height: "90px", objectFit: "cover",
                    borderRadius: "8px", border: "1.5px solid #d1d5db",
                  }}
                />
                <button
                  type="button"
                  onClick={() => onRemoveExistingImage(idx)}
                  style={{
                    position: "absolute", top: "-7px", right: "-7px",
                    width: "20px", height: "20px", borderRadius: "50%",
                    background: "#dc3545", border: "none", color: "#fff",
                    fontSize: "11px", cursor: "pointer", lineHeight: 1,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                  }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Add new product images — OPTIONAL ── */}
      <div>
        <label className="form-label fw-semibold">
          {oriEntry.existingImages?.length > 0
            ? `Add More Images (${orientation})`
            : `Product Images (${orientation})`}{" "}
          <span className="text-muted fw-normal" style={{ fontSize: "13px" }}>(optional)</span>
        </label>
        <MultipleImage onChange={onProductImagesChange} />
      </div>

      {/* ── Preview image ── */}
      <div>
        <label className="form-label fw-semibold">
          Preview Image ({orientation}){" "}
          <span className="text-muted fw-normal" style={{ fontSize: "13px" }}>(optional — used for zone mapping)</span>
        </label>

        {previewSrc ? (
          <div style={{ display: "flex", alignItems: "flex-start", gap: "16px", marginTop: "8px" }}>
            <div style={{ position: "relative", display: "inline-block" }}>
              <img src={previewSrc} alt="Preview"
                style={{
                  width: "120px", height: "90px", objectFit: "cover",
                  borderRadius: "10px", border: "2px solid #0d6efd",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              />
              <button type="button" onClick={onRemovePreview}
                style={{
                  position: "absolute", top: "-8px", right: "-8px",
                  width: "22px", height: "22px", borderRadius: "50%",
                  background: "#dc3545", border: "none", color: "#fff",
                  fontSize: "12px", cursor: "pointer", lineHeight: 1,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                }}>
                ✕
              </button>
            </div>
            <label htmlFor={`preview-${colorId}-${orientation}`}
              style={{ cursor: "pointer", fontSize: "13px", color: "#0d6efd", textDecoration: "underline", marginTop: "4px" }}>
              Replace image
              <input id={`preview-${colorId}-${orientation}`} type="file" accept="image/*"
                style={{ display: "none" }} onChange={handlePreviewInput} />
            </label>
          </div>
        ) : (
          <label htmlFor={`preview-${colorId}-${orientation}`}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: "8px", width: "160px", height: "110px",
              border: "2px dashed #adb5bd", borderRadius: "10px",
              cursor: "pointer", color: "#6c757d", fontSize: "13px",
              background: "#f8f9fa", marginTop: "6px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#0d6efd"; e.currentTarget.style.background = "#f0f5ff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#adb5bd"; e.currentTarget.style.background = "#f8f9fa"; }}>
            <span style={{ fontSize: "28px", lineHeight: 1 }}>
              {orientation === "landscape" ? "🖼️" : "📱"}
            </span>
            <span>Click to upload</span>
            <span style={{ fontSize: "11px", color: "#adb5bd" }}>JPG, PNG, WEBP</span>
            <input id={`preview-${colorId}-${orientation}`} type="file" accept="image/*"
              style={{ display: "none" }} onChange={handlePreviewInput} />
          </label>
        )}
      </div>

      {/* ── Zone drawer — only shown when preview exists ── */}
      {previewSrc && (
        <div>
          <label className="form-label fw-semibold d-block mb-1">
            Zone Mapping ({orientation})
          </label>
          <small className="text-muted d-block mb-3">
            Draw <strong>{imgCount}</strong> photo zone(s) and <strong>{txtCount}</strong> text zone(s) on the preview image.
          </small>

          {(imgCount > 0 || txtCount > 0) ? (
            <AdminZoneDrawer
              previewImageUrl={previewSrc}
              initialZones={{ photo: oriEntry.photoZones || [], text: oriEntry.textZones || [] }}
              maxPhotoZones={imgCount}
              maxTextZones={txtCount}
              onChange={onZonesChange}
            />
          ) : (
            <div style={{
              padding: "24px", border: "2px dashed #adb5bd", borderRadius: "10px",
              textAlign: "center", color: "#6c757d", fontSize: "13px", background: "#f8f9fa",
            }}>
              👆 Enter Image Upload Count and Text Count above to start drawing zones
            </div>
          )}

          {errors[`${errPrefix}_photoZones`] && (
            <div className="text-danger mt-2" style={{ fontSize: "0.875rem" }}>
              ⚠️ {errors[`${errPrefix}_photoZones`]}
            </div>
          )}
          {errors[`${errPrefix}_textZones`] && (
            <div className="text-danger mt-2" style={{ fontSize: "0.875rem" }}>
              ⚠️ {errors[`${errPrefix}_textZones`]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}